import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { banksApi } from '../services/api';
import type { BankSummary } from '../types';
import { BankPicker, useBankOptions } from './BankPicker';
import { saveErrorText } from '../utils/apiError';

/** The bank names as text, e.g. for a list column or an export. */
export const bankNamesText = (
  t: (key: string, options?: Record<string, unknown>) => string,
  banks?: BankSummary[]
) =>
  (banks ?? [])
    .map(bank => (bank.isActive ? bank.name : t('banks.inactive', { name: bank.name })))
    .join(', ');

/** The bank names, with a note on banks that can no longer be chosen. */
export function BankNames({ banks }: { banks?: BankSummary[] }) {
  const { t } = useTranslation();
  if (!banks?.length) return <span className="text-muted">{t('banks.noneChosen')}</span>;
  return <span data-testid="customer-bank-names">{bankNamesText(t, banks)}</span>;
}

type CustomerBanksProps = {
  banks?: BankSummary[];
  /** The customer's own account; without one there are no banks to change. */
  personUserId?: string | null;
  /** Admin may change a person's banks; a Hantverkare only reads them. */
  editable?: boolean;
  onSaved?: (banks: BankSummary[]) => void;
};

/**
 * A Beställare's banks on a customer (ClickUp 869eu6fpm): read-only for the
 * Hantverkare, editable for Admin.
 */
export function CustomerBanks({ banks, personUserId, editable, onSaved }: CustomerBanksProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const canEdit = Boolean(editable && personUserId);

  return (
    <div className="customer-banks" data-testid="customer-banks">
      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="fw-semibold">{t('banks.customerTitle')}:</span>
        <BankNames banks={banks} />
        {canEdit && !editing && (
          <button type="button" className="btn btn-outline-primary btn-sm customer-banks-edit" onClick={() => setEditing(true)} data-testid="customer-banks-edit">
            {t('banks.edit')}
          </button>
        )}
      </div>
      {!canEdit && <small className="text-muted">{t('banks.customerHelp')}</small>}
      {canEdit && editing && personUserId && (
        <CustomerBanksEditor
          personUserId={personUserId}
          current={banks ?? []}
          onCancel={() => setEditing(false)}
          onSaved={saved => {
            setEditing(false);
            onSaved?.(saved);
          }}
        />
      )}
    </div>
  );
}

function CustomerBanksEditor({
  personUserId,
  current,
  onCancel,
  onSaved
}: {
  personUserId: string;
  current: BankSummary[];
  onCancel: () => void;
  onSaved: (banks: BankSummary[]) => void;
}) {
  const { t } = useTranslation();
  const { banks, loadError } = useBankOptions();
  const [value, setValue] = useState(current.map(bank => bank.id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tried, setTried] = useState(false);

  const save = async () => {
    setTried(true);
    if (!value.length) return;
    setSaving(true);
    setError('');
    try {
      onSaved(await banksApi.setPersonBanks(personUserId, value));
    } catch (failure) {
      setError(saveErrorText(t, failure, 'banks.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <div className="alert alert-danger mt-2 mb-0" role="alert">{t('banks.loadError')}</div>;
  if (!banks) {
    return (
      <div className="spinner-border spinner-border-sm text-primary mt-2" role="status">
        <span className="visually-hidden">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <BankPicker banks={banks} value={value} onChange={setValue} current={current} disabled={saving} showRequired={tried} />
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <div className="d-flex flex-wrap gap-2 justify-content-end">
        <button type="button" className="btn btn-outline-secondary customer-banks-action" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn-primary customer-banks-action" onClick={() => void save()} disabled={saving} data-testid="customer-banks-save">
          {saving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}
